import { parseOrThrow } from "_shared/_utilities/zod-schema-parser";
import AHDate from "_shared/dates/date";
import DateSchema from "_shared/dates/date-schema";
import EntityMember from "_shared/entity-members/entity-member";
import Guid from "_shared/guids/guid";
import GuidSchema from "_shared/guids/guid-schema";
import SubscriberSchema from "messaging/forums/subscribers/subscriber-schema";
import Topic from "messaging/forums/topics/topic";
import ForumEntityClassSchema from "notifications/message-notifications/forum-info/forum-entity-class-schema";
import ForumName from "notifications/message-notifications/forum-info/forum-name";
import ForumNameSchema from "notifications/message-notifications/forum-info/forum-name-schema";
import { z } from "zod";

export type ForumJSON = z.infer<typeof ForumSchema.ForumJSONZ>;
export type ForumParams = z.infer<typeof ForumSchema.ForumZ>;

export default class ForumSchema {
  public static readonly ForumJSONZ = z.object({
    id: GuidSchema.GuidJSONZ.nullish(),
    name: ForumNameSchema.ForumNameJSONZ,
    entityClass: ForumEntityClassSchema.ForumEntityClassJSONZ,
    entityId: GuidSchema.GuidJSONZ.nullish(),
    context: z.string().min(1).nullish(),
    createdDate: DateSchema.DateJSONZ.nullish(),
    subscribers: z.array(SubscriberSchema.SubscriberJSONZ).nullish(),
    creator: SubscriberSchema.SubscriberJSONZ.nullish(),
    numUnreadMessages: z.number().nullish(),
    visibleUntil: DateSchema.DateJSONZ.nullish(),
    acceptsMessages: z.boolean().nullish(),
    callerMayWrite: z.boolean().nullish(),
  });

  public static readonly ForumZ = z.object({
    id: z.instanceof(Guid).optional(),
    name: z.instanceof(ForumName),
    topic: z.instanceof(Topic).optional(),
    createdDate: z.instanceof(AHDate).optional(),
    subscribers: z.array(z.instanceof(EntityMember)).optional(),
    creator: z.instanceof(EntityMember).optional(),
    numUnreadMessages: z.number().optional(),
    visibleUntil: z.instanceof(AHDate).optional(),
    acceptsMessages: z.boolean().optional(),
    callerMayWrite: z.boolean().optional(),
  });

  public static validate(values: unknown): ForumParams {
    return parseOrThrow(ForumSchema.ForumZ, values, {
      payloadSample: values,
      schemaName: "ForumSchema",
      operation: "validate",
      logError: true,
    });
  }

  public static validateJSON(json: unknown): ForumJSON {
    return parseOrThrow(ForumSchema.ForumJSONZ, json, {
      payloadSample: json,
      schemaName: "ForumSchema",
      operation: "validateJSON",
      logError: true,
    });
  }

  public static validateJSONArray(jsonArray: unknown[]): ForumJSON[] {
    return jsonArray.map((json) => ForumSchema.validateJSON(json));
  }

  public static isValid(values: unknown): values is ForumParams {
    try {
      ForumSchema.validate(values);
      return true;
    } catch {
      return false;
    }
  }

  public static isValidJSON(json: unknown): json is ForumJSON {
    try {
      ForumSchema.validateJSON(json);
      return true;
    } catch {
      return false;
    }
  }

  public static deserialize(json: unknown): ForumParams {
    const parsed = parseOrThrow(ForumSchema.ForumJSONZ, json, {
      payloadSample: json,
      schemaName: "ForumSchema",
      operation: "deserialize",
      logError: true,
    });
    return ForumSchema.codec.decode(parsed);
  }
  public static deserializeJSONArray(jsonArray: unknown): ForumParams[] {
    return parseOrThrow(z.array(ForumSchema.ForumJSONZ), jsonArray, {
      payloadSample: jsonArray,
      schemaName: "ForumSchema",
      operation: "deserializeJSONArray",
      logError: true,
    }).map((parsed) => ForumSchema.codec.decode(parsed));
  }

  public static serialize(params: ForumParams): ForumJSON {
    const validated = ForumSchema.validate(params);
    return ForumSchema.codec.encode(validated);
  }

  private static readonly codec = z.codec(ForumSchema.ForumJSONZ, ForumSchema.ForumZ, {
    decode: (data) => ({
      id: Guid.fromJSON(data.id) ?? undefined,
      name: new ForumName(ForumNameSchema.deserialize(data.name)),
      topic: Topic.fromJSON(data) ?? undefined,
      subscribers: EntityMember.fromJSONArray({ jsonArray: data.subscribers }) ?? undefined,
      creator: EntityMember.fromJSON(data.creator) ?? undefined,
      createdDate: AHDate.fromJSON(data.createdDate) ?? undefined,
      numUnreadMessages: data.numUnreadMessages ?? 0,
      visibleUntil: AHDate.fromJSON(data.visibleUntil) ?? undefined,
      acceptsMessages: data.acceptsMessages ?? undefined,
      callerMayWrite: data.callerMayWrite ?? undefined,
    }),
    encode: (forum) => ({
      id: forum.id?.toJSON(),
      name: forum.name.toJSON(),
      entityClass: ForumEntityClassSchema.validateJSON(forum.topic?.entityClass?.toJSON()),
      entityId: forum.topic?.entityId?.toJSON(),
      // Null, not "", for a forum whose topic has no context: a work agreement's forums have none, and
      // the JSON schema refuses an empty context, so every write that sent one of them failed to encode.
      context: forum.topic?.context || null,
      createdDate: forum.createdDate ? forum.createdDate.toJSON() : undefined,
      subscribers: forum.subscribers?.map((subscriber) => subscriber.toJSON()) ?? [],
      creator: forum.creator?.toJSON(),
      numUnreadMessages: forum.numUnreadMessages,
    }),
  });
}
